import { useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CommandPalette } from '@backrs_ui/grow-ui/src/components/CommandPalette';
import { noticeForError } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/noticeForError';
import { refusalProse } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/refusalProse';
import type { WriteOutcome } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/writeOutcome';
import { useDebounce } from '@backrs_ui/grow-ui/src/hooks/useDebounce';
import { CoachBadge } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/CoachBadge';
import { cohortLabel } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/namedRefLabel';
import { useGetInstitutionCoachesQuery } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.api';
import type { CoachSummary } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import {
  useGetRosterCohortsQuery,
  type AdminCohortCoach,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';
import { personInitial, personName } from '@backrs_ui/grow-ui/src/utils/personName';

import { Avatar, Box, type SxProps, type Theme, Typography } from '@mui/material';

import { cohortCoachBadgeName, cohortCoachLabel } from './cohortCoaches';
import { useAgentContext } from './CohortCoachesPalette.agentContext';

const { coachesDialog: strings } = appStrings.modules.rostersAdmin;

const SEARCH_DEBOUNCE_MS = 300;

export type CohortCoachDoors = Readonly<{
  isAttaching: boolean;
  isDetachingFor: (coachUserId: number) => boolean;
  isWriteInFlight: boolean;
  onAttach: (coach: CoachSummary, cohortName: string) => Promise<WriteOutcome>;
  onDetach: (coach: AdminCohortCoach, cohortName: string) => Promise<WriteOutcome>;
}>;

type CohortCoachesPaletteProps = Readonly<{
  open: boolean;
  onClose: () => void;
  rosterId: number;
  cohortId: number;
  /** The roster's own institution. A coach is attachable only where they hold a seat on it. */
  institutionId: number | null;
  doors: CohortCoachDoors;
}>;

export const CohortCoachesPalette = ({
  open,
  onClose,
  rosterId,
  cohortId,
  institutionId,
  doors,
}: CohortCoachesPaletteProps) => {
  const cohorts = useGetRosterCohortsQuery({ rosterId });
  const cohort = cohorts.data?.data.find((row) => row.id === cohortId) ?? null;

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [picked, setPicked] = useState<CoachSummary | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<AdminCohortCoach | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const {
    data: institutionCoaches,
    isFetching: isFetchingCoaches,
    isError: isCoachesError,
    error: coachesError,
  } = useGetInstitutionCoachesQuery(
    { q: debouncedSearch, institutionIds: institutionId === null ? undefined : [institutionId] },
    { skip: !open },
  );

  const coaches = cohort?.coaches ?? [];
  const attachedIds = new Set(coaches.map((coach) => coach.id));
  const offerable = (institutionCoaches ?? []).filter((coach) => !attachedIds.has(coach.id));
  const name = cohort === null ? '' : cohortLabel(cohort);

  useAgentContext({
    cohortId,
    cohortName: cohort === null ? null : name,
    search: debouncedSearch,
    offerable,
    attached: coaches,
    pendingRemoval,
  });

  const notice = (() => {
    if (cohorts.isError) return noticeForError(refusalProse(cohorts.error), strings.error);
    if (cohort !== null) return null;

    return cohorts.isFetching ? strings.loading : strings.gone;
  })();

  const emptyStateMessage =
    debouncedSearch.trim().length > 0 ? strings.noSearchResults : strings.noneAvailable;

  const attach = async () => {
    if (picked === null) return;

    setRefusal(null);

    const outcome = await doors.onAttach(picked, name);

    if (outcome.ok) {
      setPicked(null);
      setSearch('');

      return;
    }

    setRefusal(noticeForError(outcome.message, strings.attachFailed));
  };

  const detach = async (coach: AdminCohortCoach) => {
    setRefusal(null);

    const outcome = await doors.onDetach(coach, name);

    if (outcome.ok) {
      setPendingRemoval(null);

      return;
    }

    setRefusal(noticeForError(outcome.message, strings.detachFailed));
  };

  const footer =
    notice !== null ? (
      <Typography
        id="rosters-admin-cohort-coaches-state"
        variant="text-sm-regular"
        role={cohorts.isError ? 'alert' : 'status'}
      >
        {notice}
      </Typography>
    ) : (
      <Box sx={footerSx}>
        {refusal !== null ? (
          <Typography
            id="rosters-admin-cohort-coach-refusal"
            variant="text-sm-regular"
            role="alert"
          >
            {refusal}
          </Typography>
        ) : null}
        {isCoachesError ? (
          <Typography
            id="rosters-admin-cohort-coach-picker-error"
            variant="text-sm-regular"
            role="alert"
          >
            {noticeForError(refusalProse(coachesError), strings.searchUnavailable)}
          </Typography>
        ) : null}
        <Typography variant="text-sm-medium">{strings.current}</Typography>
        {coaches.length === 0 ? (
          <Typography
            id="rosters-admin-cohort-coaches-empty"
            variant="text-sm-regular"
            role="status"
          >
            {strings.empty}
          </Typography>
        ) : (
          <Box sx={badgesSx}>
            {coaches.map((coach) => (
              <CoachBadge
                key={coach.id}
                id={`rosters-admin-cohort-coach-${coach.id}`}
                {...cohortCoachBadgeName(coach)}
                profileImageUrl={coach.profileImageUrl}
                onDelete={() => setPendingRemoval(coach)}
                deleteId={`rosters-admin-cohort-coach-remove-${coach.id}`}
                deleteLabel={strings.removeAria(cohortCoachLabel(coach))}
                disabled={doors.isWriteInFlight}
              />
            ))}
          </Box>
        )}
        {pendingRemoval !== null ? (
          <Box sx={confirmSx} role="group" aria-label={strings.remove}>
            <Typography id="rosters-admin-cohort-coach-remove-confirm" variant="text-sm-regular">
              {strings.removeConfirm(cohortCoachLabel(pendingRemoval), name)}
            </Typography>
          </Box>
        ) : null}
      </Box>
    );

  const actions =
    pendingRemoval !== null
      ? [
          {
            id: 'rosters-admin-cohort-coach-remove-cancel',
            label: strings.cancel,
            onClick: () => setPendingRemoval(null),
          },
          {
            id: 'rosters-admin-cohort-coach-remove-submit',
            label: doors.isDetachingFor(pendingRemoval.id) ? strings.removing : strings.remove,
            disabled: doors.isWriteInFlight,
            onClick: () => void detach(pendingRemoval),
          },
        ]
      : [
          {
            id: 'rosters-admin-cohort-coach-add',
            label: doors.isAttaching ? strings.adding : strings.add,
            hierarchy: 'Primary' as const,
            disabled: picked === null || doors.isWriteInFlight || isCoachesError,
            onClick: () => void attach(),
          },
        ];

  return (
    <CommandPalette<CoachSummary>
      open={open}
      onClose={onClose}
      title={strings.title(name)}
      searchPlaceholder={strings.searchPlaceholder}
      searchValue={search}
      onSearchChange={(value) => {
        setSearch(value);
        setPicked(null);
      }}
      items={notice === null ? offerable : []}
      renderItem={(coach) => (
        <Box id={`rosters-admin-cohort-coach-option-${coach.id}`} sx={optionSx}>
          <Avatar src={coach.profile_image_url ?? undefined} alt={personName(coach)} sx={avatarSx}>
            {personInitial(coach)}
          </Avatar>
          <Typography variant="text-sm-medium">{personName(coach)}</Typography>
        </Box>
      )}
      getItemKey={(coach) => String(coach.id)}
      selectedItem={picked}
      onItemSelect={setPicked}
      loading={isFetchingCoaches}
      emptyStateMessage={notice ?? emptyStateMessage}
      footerContent={footer}
      actions={notice === null ? actions : undefined}
    />
  );
};

const footerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const badgesSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
});

const confirmSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const optionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const avatarSx: SxProps<Theme> = ({ backrs, typography }) => ({
  width: 32,
  height: 32,
  ...typography['text-sm-regular'],
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  color: backrs.colors['Colors/Foreground/fg-primary'],
});
